import React from 'react'
import { LocalStorage, Services } from '../services'
import { UsuarioContext } from './UsuarioContext'
import { AuthContext } from './AuthContext'
import { AuthAPI, EmpresaAPI, TorneioAPI } from '../api'
import { Alerts } from '../components/Simple'
import stringSimilarity from 'string-similarity'
import moment from 'moment'
import { AgendamentoContext } from './AgendamentoContext'

export const EmpresasContext = React.createContext({
  sttPg: {
    modePg: 'quadras',
    validData: {
      isValid: true, // indica se, na última verificação, o relógio local estava válido
      serverTime: null, // moment obtido da API na última verificação
      fetchedAt: null, // moment em que a API foi chamada
    },
    selectedDay: moment(),
    empresasBuffer: [],
    empresas: [],
    empresa: null,
    updateEmpresa: false,
    diaria: null,
    selectedQuadra: LocalStorage.get('selected-quadra') || null,
    avaliacao: {
      medias: {},
      avaliacoes: {
        count: null,
        rows: [],
      },
    },
    paginationAvaliacoes: {
      page: 1,
      offset: 0,
      limit: 10,
    },
    selectedHorarios: [],
    horarios: [],
    diarias: [],
    resumoPedido: false,
    tipoAcesso: null,
    messagesQuadras: [],
    cupons: [],
  },
  modaisCE: {
    modalL: false,
    modalEquipes: false,
  },
  inps: {
    search: '',
  },
  loads: {
    page: true,
    empresas: true,
    gradeHorarios: false,
    horaFx: false,
    avaliacao: true,
    diarias: true,
    cupons: true,
    solicitarAcesso: false,
    torneios: false,
    equipes: true,
  },
  // eslint-disable-next-line no-unused-vars
  BuscarQuadrasEmpresa: async idempresa => {},
  // eslint-disable-next-line no-unused-vars
  BuscarEmpresaEVerificarAcesso: async username => {},
  actionsCtxEmps: {
    ValidarDataDispositivo: async () => {},
    // eslint-disable-next-line no-unused-vars
    SolicitarAcessoCentroEsportivo: async motivo => {},
    // eslint-disable-next-line no-unused-vars
    BuscarEmpresas: async cidade => {},
    // eslint-disable-next-line no-unused-vars
    BuscarEmpresasCidade: async cidade => {},
    // eslint-disable-next-line no-unused-vars
    CriarReagendamento: async pedido => {},
    // eslint-disable-next-line no-unused-vars
    BuscarEmpresaUsername: async username => {},
    // eslint-disable-next-line no-unused-vars
    VerificaStatusEmpresa: async emp => {},
    // eslint-disable-next-line no-unused-vars
    BuscarAvaliacoesEmpresa: async idempresa => {},
    // eslint-disable-next-line no-unused-vars
    BuscarGradeHorarios: async () => {},
    BuscarDiariasEmpresa: async () => {},
    BuscarCuponsAtivosEmpresa: async () => {},
    // eslint-disable-next-line no-unused-vars
    BuscarTorneio: async idtorneio => {},
    VerificarAcessoUser: async () => {},
    // eslint-disable-next-line no-unused-vars
    ShowRegraCancelamento: regra => {},
    // eslint-disable-next-line no-unused-vars
    SelectEmpresa: emp => {},
    // eslint-disable-next-line no-unused-vars
    handleSttPg: (key, val) => {},
    // eslint-disable-next-line no-unused-vars
    handleModeSttPg: buffer => {},
    // eslint-disable-next-line no-unused-vars
    handleInps: (key, val) => {},
    // eslint-disable-next-line no-unused-vars
    handleModaisCE: (key, val) => {},
    // eslint-disable-next-line no-unused-vars
    handleLoads: (key, val) => {},
    // eslint-disable-next-line no-unused-vars
    handleQuadraSelected: quadra => {},
    clearPaginationAvaliacoes: () => {},
    // eslint-disable-next-line no-unused-vars
    handleSelectHorario: (horario, isSelected) => {},
    clearSelectedHorarios: () => {},
    // eslint-disable-next-line no-unused-vars
    toggleResumoDrawer: state => {},
    confirmarPedido: () => {},
  },
  getCurrentDay: () => moment(),
})

export default function EmpresasContextProvider({ children }) {
  const { sttAuthCtx } = React.useContext(AuthContext)
  const { userUCtx, actionsUCtx } = React.useContext(UsuarioContext)
  const { sttCtxAgend } = React.useContext(AgendamentoContext)
  const reagendamento = sttCtxAgend.reagendamento
  const daySelected = LocalStorage.get('day-selected')
  let daySel = moment() // Default to current date
  if (daySelected) {
    daySel = moment(daySelected)
  }

  const [sttPg, setSttPg] = React.useState({
    modePg: LocalStorage.get('sttPg.modePg') || 'empresas',
    validData: {
      isValid: true,
      serverTime: null,
      fetchedAt: null,
    },
    selectedDay: daySel,
    empresasBuffer: [],
    empresas: [],
    empresa: null,
    updateEmpresa: false,
    diaria: null,
    selectedQuadra: LocalStorage.get('selected-quadra') || null,
    messagesQuadras: [], // [{idquadra, messagem}, ...]
    cupons: [],
    horarios: [],
    selectedHorarios: [],
    diarias: [],
    resumoPedido: false,
    tipoAcesso: null,
    paginationAvaliacoes: {
      page: 1,
      offset: 0,
      limit: 10,
    },
    avaliacao: {
      medias: {},
      avaliacoes: {
        count: null,
        rows: [],
      },
    },
  })

  const [inps, setInps] = React.useState({
    search: '',
  })

  const [loads, setLoads] = React.useState({
    page: true,
    empresas: true,
    horaFx: false,
    gradeHorarios: false,
    avaliacao: true,
    diarias: true,
    solicitarAcesso: false,
    cupons: true,
    torneios: false,
    equipes: true,
  })

  const [modaisCE, setModaisCE] = React.useState({
    modalL: false,
    modalEquipes: false,
  })

  React.useEffect(() => {
    LocalStorage.set('sttPg.modePg', sttPg.modePg)
    if (sttPg.modePg !== 'checkout') setSttPg(sttPg => ({ ...sttPg, selectedHorarios: [] }))
  }, [sttPg.modePg])

  async function VerificarAcessoUser(idempresa) {
    const res = await EmpresaAPI.VerificaAcessoEmpresa(idempresa, userUCtx.usuario.idusuario)

    if (res.error) {
      Alerts.swal('warning', 'Aviso', res.message)
      return { status: null }
    }
    if (res.length === 0) return { status: true, registro: null }
    return { status: true, registro: res[0] }
  }

  React.useEffect(() => {
    if (sttAuthCtx.isLogged) ValidarDataDispositivo()
  }, [sttAuthCtx.isLogged])

  async function BuscarQuadrasEmpresa(idempresa) {
    const ress = await EmpresaAPI.BuscarQuadrasEmpresa(idempresa)
    if (ress.error) {
      Alerts.swal(
        'warning',
        'Aviso',
        'Erro ao buscar as quadras da empresa. Por favor, tente novamente mais tarde.',
      )
      return { quadras: [] }
    }
    return { quadras: ress.filter(quadra => quadra.status === 1) }
  }
  async function BuscarEmpresaEVerificarAcesso(username) {
    setLoads(loads => ({ ...loads, page: true }))
    const empresa = await actionsCtxEmps.BuscarEmpresaUsername(username)
    if (empresa) {
      await actionsCtxEmps.VerificaStatusEmpresa(empresa)
    } else {
      // console.error('Erro ao buscar empresa :: ', username)
    }
    setLoads(loads => ({ ...loads, page: false }))
  }
  async function ValidarDataDispositivo() {
    try {
      const res = await AuthAPI.getDateTimer()
      if (!res.time) return

      const serverMoment = moment.parseZone(res.time).local()
      const nowLocal = moment()
      const diffMin = Math.abs(serverMoment.diff(nowLocal, 'minutes'))

      // Sempre armazenamos serverTime e fetchedAt
      //
      if (diffMin > 15) {
        setSttPg(prev => ({
          ...prev,
          validData: {
            isValid: false,
            serverTime: serverMoment,
            fetchedAt: nowLocal,
          },
          selectedDay: serverMoment, // Atualiza o selectedDay para o horário do servidor
        }))
      } else {
        // relógio OK
        setSttPg(prev => ({
          ...prev,
          validData: {
            isValid: true,
            serverTime: serverMoment,
            fetchedAt: nowLocal,
          },
        }))
      }
    } catch (err) {
      //console.error('Erro ao validar data/hora do dispositivo:', err)
      setSttPg(prev => ({
        ...prev,
        validData: {
          isValid: true,
          serverTime: null, // Não temos o horário do servidor
          fetchedAt: moment(), // Usa o momento local como referência
        },
      }))
    }
  }
  function getCurrentDay() {
    const { isValid, serverTime, fetchedAt } = sttPg.validData || {}
    // Se nunca fez fetch, usa momento local (até a resposta chegar)
    if (!fetchedAt) {
      return moment()
    }
    // Se for isValid = true, verifica se ainda esta ok!, se não estiver consome novamente para validar!
    if (isValid && serverTime) {
      const localMoment = moment()
      const currentDay = moment(serverTime).local() // Converte serverTime para local
      // Se for mais de 24 horas fora de sincronia, considera inválido
      if (Math.abs(currentDay.diff(localMoment, 'hours')) > 24) {
        ValidarDataDispositivo() // Revalida a data/hora do dispositivo
      }
    }
    // Se estava fora de sincronia, recalcula a "hora do servidor":
    if (isValid === false && serverTime) {
      const elapsedMs = moment().diff(fetchedAt)
      return moment(serverTime).add(elapsedMs, 'milliseconds')
    }
    // Caso normal (está em sincronia), retorna momento local
    return moment()
  }
  const actionsCtxEmps = {
    ValidarDataDispositivo,
    BuscarEmpresasCidade: async cidade => {
      const normalizeString = str => {
        return str
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      }
      setLoads(loads => ({ ...loads, empresas: true }))

      const reqCity = await EmpresaAPI.BuscarCidade(cidade)
      if (reqCity.error) {
        setSttPg(sttPg => ({ ...sttPg, empresas: [], empresasBuffer: [], modePg: 'errorComunic' }))
        setLoads(loads => ({ ...loads, empresas: false }))
        return reqCity
      }

      if (reqCity.length) {
        let cit
        const cidadeNormalizada = normalizeString(cidade)

        if (reqCity.length > 1) {
          // Verifique primeiro se há uma correspondência exata após normalizar
          cit = reqCity.find(c => normalizeString(c.nomeCidade) === cidadeNormalizada)

          if (!cit) {
            // Se não houver correspondência exata, procure cidades que começam com o termo normalizado
            const matches = reqCity.filter(c =>
              normalizeString(c.nomeCidade).startsWith(cidadeNormalizada),
            )

            if (matches.length > 0) {
              // Se houver cidades que começam com o termo, escolha a mais parecida entre elas
              cit = matches.sort(
                (a, b) =>
                  stringSimilarity.compareTwoStrings(
                    normalizeString(b.nomeCidade),
                    cidadeNormalizada,
                  ) -
                  stringSimilarity.compareTwoStrings(
                    normalizeString(a.nomeCidade),
                    cidadeNormalizada,
                  ),
              )[0]
            } else {
              // Caso nenhuma cidade comece com o termo, calcule a similaridade geral como último recurso
              const rankedCities = reqCity.map(c => ({
                ...c,
                similarity: stringSimilarity.compareTwoStrings(
                  normalizeString(c.nomeCidade),
                  cidadeNormalizada,
                ),
              }))

              // Ordene pela similaridade
              rankedCities.sort((a, b) => b.similarity - a.similarity)
              cit = rankedCities[0]
            }
          }
        } else {
          // Se só houver uma cidade, use-a diretamente
          cit = reqCity[0]
        }

        //console.log('Cidade mais parecida', cit)

        const reqs = await EmpresaAPI.BuscarEmpresasCidade(cit.idcidade)

        if (reqs.error) {
          return reqs
        }
        // console.log('Empresas', reqs)
        actionsUCtx.handleCidade(cit)
        // TESTE introduzir torneios nas empresas: e adiciona o json de teste na 525
        const emps = reqs

        // reqs.map(emp => {
        //   extrai os torneios das quadras e joga na empresa
        //   const torneios = emp.quadras.reduce((acc, qd) => {
        //     if (qd.torneios?.length) {
        //       acc.push(...qd.torneios)
        //     }
        //     // não retorne torneios duplicados idtorneio
        //     acc = acc.filter((item, index, self) => self.findIndex(t => t.idtorneio === item.idtorneio) === index)
        //     return acc
        //   }, [])
        //   return { ...emp, torneios: torneios || [] }
        // })

        setSttPg(sttPg => ({ ...sttPg, empresas: emps, empresasBuffer: emps, modePg: 'empresas' }))
        setLoads(loads => ({ ...loads, empresas: false }))

        return cit
      } else {
        setSttPg(sttPg => ({ ...sttPg, empresas: [], empresasBuffer: [], modePg: 'empresas' }))
        setLoads(loads => ({ ...loads, empresas: false }))
        return { error: true, message: 'Cidade não encontrada' }
      }
    },
    // TODO refatorar para simplificar responsabilidades da função BuscarEmpresaUsername
    BuscarEmpresaUsername: async username => {
      const res = await EmpresaAPI.BuscarEmpresaUsername(username)
      //console.log('Busca Empresa', res)
      if (res.error) {
        setSttPg(sttPg => ({ ...sttPg, modePg: 'errorComunic' }))
        setLoads(loads => ({ ...loads, page: false }))
        return Alerts.swal(
          'warning',
          'Aviso',
          'Parece que estamos enfrentando problemas de comunicação. Por favor, verifique sua conexão com a internet e tente novamente.',
        )
      }
      if (!res) {
        setSttPg(sttPg => ({ ...sttPg, modePg: 'errorLink' }))
        setLoads(loads => ({ ...loads, page: false }))
        return false
      }
      return res
    },
    VerificaStatusEmpresa: async emp => {
      if (emp?.status && emp?.licenca?.status) {
        if (!emp.configurado) {
          const empresa = { ...emp, quadras: [] }
          setSttPg(sttPg => ({ ...sttPg, empresa, modePg: 'config' }))
          setLoads(loads => ({ ...loads, page: false }))
          return false
        }
        if (sttAuthCtx.isLogged) {
          const tipoAcesso = await VerificarAcessoUser(emp.idempresa)

          if (tipoAcesso.status && tipoAcesso.registro !== null) {
            if (tipoAcesso.registro.status === 'APROVADO') {
              const reqsQdr = await BuscarQuadrasEmpresa(emp.idempresa)
              // let trs = reqsQdr.quadras.reduce((acc, qd) => {
              //   if (qd.torneios.length) {
              //     acc.push(...qd.torneios)
              //   }
              //   return acc
              // }, [])
              // trs = trs.filter((item, index, self) => self.findIndex(t => t.idtorneio === item.idtorneio) === index)
              LocalStorage.set('empresa-user', { ...emp, quadras: reqsQdr.quadras }) //, torneios: trs})
              const modePgBuf = LocalStorage.get('sttPg.modePg')
              setSttPg(sttPg => ({
                ...sttPg,
                modePg: modePgBuf
                  ? modePgBuf === 'empresas' ||
                    modePgBuf === 'errorLink' ||
                    modePgBuf === 'errorComunic' ||
                    modePgBuf === 'noAccess' ||
                    modePgBuf === 'config'
                    ? 'quadras'
                    : modePgBuf
                  : 'quadras',
                tipoAcesso: tipoAcesso.registro.status,
                empresa: { ...emp, quadras: reqsQdr.quadras }, //torneios: trs },
                selectedQuadra: LocalStorage.get('selected-quadra') || null,
                selectedHorarios: [],
              }))
            } else {
              setSttPg(sttPg => ({
                ...sttPg,
                empresa: emp,
                modePg: 'noAccess',
                tipoAcesso: tipoAcesso.registro.status,
              }))
            }
          } else {
            if (tipoAcesso.status) {
              if (emp.visibilidade === 'PRIVADO' || emp.visibilidade === 'PRIVADA') {
                setSttPg(sttPg => ({
                  ...sttPg,
                  empresa: emp,
                  modePg: 'noAccess',
                  tipoAcesso: null,
                }))
              } else {
                const reqsQdr = await BuscarQuadrasEmpresa(emp.idempresa)
                // let trs = reqsQdr.quadras.reduce((acc, qd) => {
                //   if (qd.torneios.length) {
                //     acc.push(...qd.torneios)
                //   }
                //   return acc
                // }, [])
                // trs = trs.filter((item, index, self) => self.findIndex(t => t.idtorneio === item.idtorneio) === index)
                LocalStorage.set('empresa-user', { ...emp, quadras: reqsQdr.quadras }) //torneios: trs })
                //console.log('Empresa', res, '\nQuadras', reqsQdr.quadras)
                const modePgBuf = LocalStorage.get('sttPg.modePg')
                setSttPg(sttPg => ({
                  ...sttPg,
                  empresa: { ...emp, quadras: reqsQdr.quadras }, //torneios: trs },
                  selectedQuadra: LocalStorage.get('selected-quadra') || null,
                  modePg: modePgBuf
                    ? modePgBuf === 'empresas' ||
                      modePgBuf === 'errorLink' ||
                      modePgBuf === 'errorComunic' ||
                      modePgBuf === 'noAccess' ||
                      modePgBuf === 'config'
                      ? 'quadras'
                      : modePgBuf
                    : 'quadras',
                  tipoAcesso: null,
                  selectedHorarios: [],
                }))
              }
            } else {
              setSttPg(sttPg => ({ ...sttPg, empresa: emp, modePg: 'noAccess', tipoAcesso: null }))
            }
          }
        } else if (emp.visibilidade === 'PRIVADO' || emp.visibilidade === 'PRIVADA') {
          setSttPg(sttPg => ({ ...sttPg, empresa: emp, modePg: 'noAccess', tipoAcesso: null }))
        } else {
          const reqsQdr = await BuscarQuadrasEmpresa(emp.idempresa)
          // let trs = reqsQdr.quadras.reduce((acc, qd) => {
          //   if (qd.torneios.length) {
          //     acc.push(...qd.torneios)
          //   }
          //   return acc
          // }, [])
          // trs = trs.filter((item, index, self) => self.findIndex(t => t.idtorneio === item.idtorneio) === index)
          LocalStorage.set('empresa-user', { ...emp, quadras: reqsQdr.quadras }) //, torneios: trs })
          const modePgBuf = LocalStorage.get('sttPg.modePg')
          setSttPg(sttPg => ({
            ...sttPg,
            empresa: { ...emp, quadras: reqsQdr.quadras }, //, torneios: trs },
            modePg: modePgBuf
              ? modePgBuf === 'empresas' ||
                modePgBuf === 'errorLink' ||
                modePgBuf === 'errorComunic' ||
                modePgBuf === 'noAccess' ||
                modePgBuf === 'config'
                ? 'quadras'
                : modePgBuf
              : 'quadras',
            tipoAcesso: null,
            selectedQuadra: LocalStorage.get('selected-quadra') || null,
            selectedHorarios: [],
          }))
        }
      } else {
        // Mostra mensagem de empresa desativada
        setSttPg(sttPg => ({
          ...sttPg,
          empresa: emp,
          modePg: 'noAccess',
          tipoAcesso: 'DESATIVADO',
        }))
      }
    },
    SelectEmpresa: emp => {
      setSttPg(sttPg => ({ ...sttPg, empresa: emp, modePg: 'quadras' }))
    },
    BuscarAvaliacoesEmpresa: async idempresa => {
      {
        sttPg.paginationAvaliacoes.offset === 0 &&
          setLoads(loads => ({ ...loads, avaliacao: true }))
      }
      const res = await EmpresaAPI.BuscarAvaliacoesEmpresa(
        idempresa,
        sttPg.paginationAvaliacoes.offset,
        sttPg.paginationAvaliacoes.limit,
      )
      setLoads(loads => ({ ...loads, avaliacao: false }))
      if (res.error) {
        return Alerts.error(res.message)
      }
      if (res) {
        setSttPg(sttPg => ({
          ...sttPg,
          avaliacao: {
            medias: res.avaliacao.medias,
            avaliacoes: {
              count: res.avaliacao.avaliacoes.count,
              rows: [...sttPg.avaliacao.avaliacoes.rows, ...res.avaliacao.avaliacoes.rows],
            },
          },
          paginationAvaliacoes: {
            ...sttPg.paginationAvaliacoes,
            offset: sttPg.paginationAvaliacoes.offset + res.avaliacao.avaliacoes.rows.length,
            page: res.avaliacao.avaliacoes.rows.length / sttPg.paginationAvaliacoes.limit,
          },
        }))
        return res
      }
    },
    BuscarGradeHorarios: async qdr => {
      setLoads(loads => ({ ...loads, gradeHorarios: true }))

      // 1) Obtém “hoje” ajustado pelo servidor
      const currentDay = getCurrentDay() // moment() local ou baseado em serverTime + elapsed
      // Também verifica se o horário não mudou depois de renderizado ou seja se o validData.isValid é true, faz uma verificação se realmente a data bate com o horário do dispositivo
      if (sttPg.validData.isValid) {
        const localMoment = moment()
        if (!currentDay.isSame(localMoment, 'day')) {
          // Se o horário do servidor não bate com o local, atualiza validData
          setSttPg(prev => ({
            ...prev,
            validData: {
              ...prev.validData,
              isValid: false, // Indica que o relógio local está fora de sincronia
              serverTime: currentDay,
              fetchedAt: localMoment,
            },
          }))
        }
      }
      let paramQdr = null
      if (sttAuthCtx.isMobile)
        paramQdr = qdr
          ? JSON.stringify([qdr.idquadra])
          : JSON.stringify([sttPg.selectedQuadra?.idquadra])
      else
        paramQdr = JSON.stringify(
          sttPg.empresa.quadras.filter(qd => qd.status === 1).map(qd => qd.idquadra),
        )

      // verifica se quadras estão vazias
      if (!paramQdr) {
        setLoads(loads => ({ ...loads, gradeHorarios: false }))
        return
      }

      const res = await EmpresaAPI.BuscarGradeHorarios(
        paramQdr,
        moment(sttPg.selectedDay).format('DD/MM/YYYY'),
        sttAuthCtx.isLogged ? userUCtx.usuario.idusuario : '-1',
      )

      if (!res.retorno) {
        // limpa grades/horários exatamente como antes...
        if (qdr) {
          setSttPg(sttPg => ({ ...sttPg, selectedQuadra: { ...qdr, grades: [], horarios: [] } }))
        } else if (sttPg.selectedQuadra) {
          setSttPg(sttPg => ({
            ...sttPg,
            selectedQuadra: { ...sttPg.selectedQuadra, grades: [], horarios: [] },
          }))
        } else {
          setSttPg(sttPg => ({
            ...sttPg,
            empresa: {
              ...sttPg.empresa,
              quadras: sttPg.empresa.quadras.map(qd => ({ ...qd, grades: [], horarios: [] })),
            },
          }))
        }
        setLoads(loads => ({ ...loads, gradeHorarios: false }))
        return res
      }

      // 2) Mapeia quadras aplicando filtros com `currentDay`
      let quadras = sttPg.empresa.quadras.map(qd => {
        const matchingQuadra = res.registro.find(r => r.quadra_idquadra === qd.idquadra)
        if (!matchingQuadra) {
          return { ...qd, grades: [], horarios: [] }
        }

        // atribui IDs únicos temporários a cada horário
        matchingQuadra.horarios.forEach(
          horario => (horario.idHorario = Math.floor(Math.random() * 1000000)),
        )

        // Verifica se o dia selecionado é “hoje” (ajustado pelo servidor)
        if (sttPg.selectedDay.isSame(currentDay, 'day')) {
          // Se existir antecedência mínima, filtra a partir de currentDay + antecedência
          if (qd.regraAgendamento.minimoAntecedenciaAgendamento > 0) {
            const limite = currentDay
              .clone()
              .add(qd.regraAgendamento.minimoAntecedenciaAgendamento, 'minutes')
            return {
              ...qd,
              grades: matchingQuadra.grades,
              horarios: matchingQuadra.horarios.filter(horario =>
                moment(horario.dataInicio).isAfter(limite),
              ),
            }
          } else {
            // sem antecedência mínima: filtra tudo estritamente depois de currentDay
            return {
              ...qd,
              grades: matchingQuadra.grades,
              horarios: matchingQuadra.horarios.filter(horario =>
                moment(horario.dataInicio).isAfter(currentDay),
              ),
            }
          }
        }

        // se não for hoje (ou seja, dia futuro ou passado), exibe toda a lista
        return {
          ...qd,
          grades: matchingQuadra.grades,
          horarios: matchingQuadra.horarios,
        }
      })

      const empresa = { ...sttPg.empresa, quadras }
      let messagesQuadras = []

      // 3) Para cada quadra, aplica regras de desativação por datas
      for (let quadra of quadras) {
        // a) Desativa se houver “desativaGrades” no intervalo
        if (quadra.desativaGrades?.length) {
          for (let item of quadra.desativaGrades) {
            const selectedDayStart = moment(sttPg.selectedDay).startOf('day')
            const dataInicio = moment(item.dataInicio).startOf('day')
            const dataFim = moment(item.dataFim).startOf('day')
            if (selectedDayStart.isBetween(dataInicio, dataFim, undefined, '[]')) {
              quadra.horarios = []
              messagesQuadras.push({
                idquadra: quadra.idquadra,
                message: 'Grade desativada para este dia',
              })
            }
          }
        }

        // b) Verifica limite máximo de dias para agendar (apenas se não for mobile)
        if (!sttAuthCtx.isMobile && quadra.regraAgendamento.maximoTempoAgendamento > 0) {
          // em vez de moment(), usar getCurrentDay()
          const todayStart = currentDay.clone().startOf('day')
          const selectedStart = moment(sttPg.selectedDay).startOf('day')
          const diffDias = selectedStart.diff(todayStart, 'days')

          if (diffDias >= quadra.regraAgendamento.maximoTempoAgendamento) {
            quadra.horarios = []
            messagesQuadras.push({
              idquadra: quadra.idquadra,
              message: 'Limite de dias para agendar excedido nesta quadra',
            })
          }
        }

        // c) Regra de reagendamento (mesma ideia: usar currentDay para calcular)
        if (Object.keys(reagendamento).length > 0 && !sttAuthCtx.isMobile) {
          const referencia =
            sttCtxAgend.pedido?.reagendamentos?.length === 0
              ? moment(sttCtxAgend.pedido.dataInicio).startOf('day')
              : moment(sttCtxAgend.pedido.reagendamentos[0].dataInicio).startOf('day')
          const selectedStart = moment(sttPg.selectedDay).startOf('day')
          const diffReagend = selectedStart.diff(referencia, 'days')
          if (diffReagend > 30) {
            quadra.horarios = []
            messagesQuadras.push({
              idquadra: quadra.idquadra,
              message: 'Limite de dias para um reagendamento é de 30 dias',
            })
          }
        }
      }

      // 4) Atualiza estado conforme mobile ou web
      if (qdr) {
        const selectedQuadra = quadras.find(qd => qd.idquadra === qdr.idquadra)
        LocalStorage.set('selected-quadra', selectedQuadra)
        setSttPg(sttPg => ({ ...sttPg, empresa, selectedQuadra, messagesQuadras }))
      } else if (sttAuthCtx.isMobile) {
        const selectedQuadra = quadras.find(qd => qd.idquadra === sttPg.selectedQuadra.idquadra)
        LocalStorage.set('selected-quadra', selectedQuadra)
        setSttPg(sttPg => ({ ...sttPg, empresa, selectedQuadra, messagesQuadras }))
      } else {
        LocalStorage.remove('selected-quadra')
        setSttPg(sttPg => ({ ...sttPg, empresa, selectedQuadra: null, messagesQuadras }))
      }

      setLoads(loads => ({ ...loads, gradeHorarios: false }))
    },
    BuscarDiariasEmpresa: async () => {
      setLoads(loads => ({ ...loads, diarias: true }))
      const res = await EmpresaAPI.BuscarDiariasEmpresa(
        sttPg.empresa.idempresa,
        moment(sttPg.selectedDay).day() + 1,
      )
      if (res?.error) {
        return false
      }
      setSttPg(sttPg => ({ ...sttPg, diarias: res }))
      setLoads(loads => ({ ...loads, diarias: false }))
    },
    BuscarCuponsAtivosEmpresa: async () => {
      setLoads(loads => ({ ...loads, cupons: true }))
      const res = await EmpresaAPI.BuscarCuponsAtivosEmpresa(sttPg.empresa.idempresa)
      if (res.error) {
        return null //console.error('Erro ao buscar cupons')
      }
      setSttPg(sttPg => ({ ...sttPg, cupons: res }))
      setLoads(loads => ({ ...loads, cupons: false }))
    },
    BuscarTorneio: async idtorneio => {
      const req = await TorneioAPI.BuscarTorneioPorId({ idtorneio, buscarEmpresa: true })
      if (req.error) {
        return Alerts.swal('warning', 'Aviso', req.message)
      }
      if (req.idtorneio) {
        const emp = req.quadras[0].empresa
        emp.torneios = [req]
        setSttPg(sttPg => ({ ...sttPg, empresa: emp }))
        req.empresa = emp
        return req
      } else {
        return Alerts.swal('warning', 'Aviso', 'Torneio não encontrado')
      }
    },
    SolicitarAcessoCentroEsportivo: async motivo => {
      const req = await EmpresaAPI.SolicitarAcessoCentroEsportivo(
        sttPg.empresa.idempresa,
        userUCtx.usuario.idusuario,
        motivo,
      )
      if (req.error) {
        return Alerts.swal('error', 'Erro', req.message)
      }
      setSttPg(sttPg => ({ ...sttPg, tipoAcesso: 'SOLICITADO' }))
      return Alerts.swal(
        'success',
        'Solicitação enviada',
        'Aguarde a resposta do gestor do centro esportivo',
      )
    },
    ShowRegraCancelamento: regra => {
      if (regra.minutosAntecedencia === 0)
        return <p>{`Cancelamento GRATUITO até o horário do agendamento.`}</p>
      else if (
        regra.minutosAntecedencia !== 0 &&
        regra.porcentagemMulta === 0 &&
        regra.permitirCancelamentoPosHorario === 1
      ) {
        return (
          <p>{`'Você pode cancelar até ${Services.RetornaMinutagem(regra.minutosAntecedencia)} antes do horário.`}</p>
        )
      } else if (
        regra.minutosAntecedencia !== 0 &&
        regra.porcentagemMulta > 0 &&
        regra.permitirCancelamentoPosHorario === 1
      ) {
        return (
          <>
            <p>{`Você pode cancelar GRATUITAMENTE até ${Services.RetornaMinutagem(regra.minutosAntecedencia)} antes do horário.`}</p>
            <p>{`Se você cancelar após o horário limite, será cobrada uma taxa de ${regra.porcentagemMulta}% do valor total do agendamento.`}</p>
            <p>{`Se o pagamento for um sinal, ele será a multa.`}</p>
          </>
        )
      } else if (regra.minutosAntecedencia !== 0 && regra.permitirCancelamentoPosHorario === 0) {
        return (
          <>
            <p>{`Você pode cancelar GRATUITAMENTE até ${Services.RetornaMinutagem(regra.minutosAntecedencia)} antes do horário.`}</p>
            <p>{`Após o horário limite NÃO será possível cancelar e você deverá entrar em contato com o Gestor pelo chat.`}</p>
          </>
        )
      } else return <p>{regra.permitirCancelamentoPosHorario}</p>
    },
    handleSttPg: (key, val) => {
      if (key === 'empresa') {
        if (val) LocalStorage.set('empresa-user', val)
        else LocalStorage.remove('empresa-user')
      }
      setSttPg(sttPg => ({ ...sttPg, [key]: val }))
    },
    handleModeSttPg: buffer => {
      setSttPg(sttPg => ({ ...sttPg, ...buffer }))
    },
    handleInps: (key, val) => {
      setInps(inps => ({ ...inps, [key]: val }))
    },
    handleLoads: (key, val) => {
      setLoads(loads => ({ ...loads, [key]: val }))
    },
    handleModaisCE: (key, val) => {
      setModaisCE(modaisCE => ({ ...modaisCE, [key]: val }))
    },
    handleQuadraSelected: quadra => {
      if (quadra === null) {
        LocalStorage.remove('selected-quadra')
      } else {
        LocalStorage.set('selected-quadra', quadra)
      }
      setSttPg(sttPg => ({ ...sttPg, selectedQuadra: quadra }))
    },
    clearPaginationAvaliacoes: () => {
      setSttPg(sttPg => ({
        ...sttPg,
        paginationAvaliacoes: { ...sttPg.paginationAvaliacoes, offset: 0, page: 1 },
        avaliacao: { ...sttPg.avaliacao, avaliacoes: { count: null, rows: [] } },
      }))
    },
    handleSelectHorario: (horario, isSelected) => {
      let selectedHorariosSorted = sttPg.selectedHorarios.sort((a, b) =>
        moment(a.dataInicio).diff(moment(b.dataInicio)),
      )

      const checkQtdMax = (horarioSelecionado, horariosAtuais) => {
        let grade = null
        if (sttPg.selectedQuadra)
          grade = sttPg.selectedQuadra.grades.find(g => g.idgrade === horarioSelecionado.idgrade)
        else {
          const quadra = sttPg.empresa.quadras.find(q =>
            q.horarios.find(h => h.idHorario === horarioSelecionado.idHorario),
          )
          if (quadra) grade = quadra.grades.find(g => g.idgrade === horarioSelecionado.idgrade)
          else return true
        }
        if (grade && grade.qtdMax !== null) {
          const horariosDaGrade = horariosAtuais.filter(
            h => h.idgrade === horarioSelecionado.idgrade,
          )
          const totalMinutes = horariosDaGrade.reduce((sum, h) => {
            const duration = moment(h.dataFinal).diff(moment(h.dataInicio), 'minutes')
            return sum + duration
          }, 0)

          if (
            totalMinutes +
              moment(horarioSelecionado.dataFinal).diff(
                moment(horarioSelecionado.dataInicio),
                'minutes',
              ) >
            grade.qtdMax
          ) {
            const duration = moment.duration(grade.qtdMax, 'minutes')
            const hours = Math.floor(duration.asHours())
            const minutes = duration.minutes()

            Alerts.swal(
              'warning',
              'Aviso',
              `Esse período permite no máximo ${hours} ${hours === 1 ? 'hora' : 'horas'}${minutes > 0 ? ` e ${minutes} minutos` : ''}`,
            )
            return false
          }
        }
        return true
      }
      if (isSelected) {
        if (
          selectedHorariosSorted.length === 0 ||
          selectedHorariosSorted.some(
            selected =>
              moment(selected.dataFinal).isSame(moment(horario.dataInicio)) ||
              moment(selected.dataInicio).isSame(moment(horario.dataFinal)),
          )
        ) {
          if (!checkQtdMax(horario, sttPg.selectedHorarios)) return false
          let array = [...sttPg.selectedHorarios, horario]

          if (array.length > 1)
            array = array.sort((a, b) => moment(a.dataInicio).diff(moment(b.dataInicio)))

          let quadra = sttPg.selectedQuadra
          if (!quadra) {
            quadra = sttPg.empresa.quadras.find(qd =>
              qd.horarios.find(h => h.idHorario === horario.idHorario),
            )
          }
          LocalStorage.set('selected-quadra', quadra)
          setSttPg(sttPg => ({ ...sttPg, selectedHorarios: array, selectedQuadra: quadra }))
        } else {
          Alerts.swal('warning', 'Erro', 'Só é possível selecionar horários em sequência')
        }
      } else {
        if (selectedHorariosSorted.length === 1) {
          let selectedQuadra = sttPg.selectedQuadra
          if (!sttAuthCtx.isMobile) {
            selectedQuadra = null
            LocalStorage.remove('selected-quadra')
          }
          setSttPg(sttPg => ({ ...sttPg, selectedHorarios: [], selectedQuadra }))
        } else if (
          selectedHorariosSorted[0].dataInicio === horario.dataInicio ||
          selectedHorariosSorted[sttPg.selectedHorarios.length - 1].dataInicio ===
            horario.dataInicio
        ) {
          setSttPg(sttPg => ({
            ...sttPg,
            selectedHorarios: sttPg.selectedHorarios.filter(
              selected => selected.dataInicio !== horario.dataInicio,
            ),
          }))
        } else {
          return Alerts.swal('error', 'Erro', 'Não é possível marcar horários não consecutivos')
        }
      }
    },
    clearSelectedHorarios: () => {
      if (sttPg.modePg === 'checkout') {
        setSttPg(prevSttPg => ({ ...prevSttPg, selectedHorarios: [], modePg: 'quadras' }))
      } else {
        setSttPg(prevSttPg => ({ ...prevSttPg, selectedHorarios: [] }))
      }
    },
    toggleResumoDrawer: state => {
      setSttPg(sttPg => ({ ...sttPg, resumoPedido: state }))
    },
    confirmarPedido: () => {
      setSttPg(sttPg => ({ ...sttPg, modePg: 'checkout', resumoPedido: false }))
    },
  }

  React.useEffect(() => {
    if (inps.search.length) {
      setSttPg(sttPg => ({
        ...sttPg,
        empresas: sttPg.empresasBuffer.filter(emp =>
          emp.nome.toLowerCase().includes(inps.search.toLowerCase()),
        ),
      }))
    } else {
      setSttPg(sttPg => ({ ...sttPg, empresas: sttPg.empresasBuffer }))
    }
  }, [inps.search])

  return (
    <EmpresasContext.Provider
      value={{
        BuscarQuadrasEmpresa,
        BuscarEmpresaEVerificarAcesso,
        sttPg,
        modaisCE,
        inps,
        loads,
        actionsCtxEmps,
        getCurrentDay,
      }}
    >
      {children}
    </EmpresasContext.Provider>
  )
}
